// =============================================================================
// Marketing entrypoint — Stimulus core + LAZY Vue islands.
//
// Marketing pages are mostly server-rendered HAML. This entry ships shared
// Stimulus controls synchronously; optional SSR widgets load only where present; everything
// Vue — the runtime, Pinia, Vue Query and every island component — lives in
// ../marketing-vue/mount_islands.js and is imported dynamically:
//
//   * pages with real content islands (#marketing-vue root, VIN demo, MCP
//     chat, dealer preview, demo cards) load the chunk on DOMContentLoaded —
//     those islands ARE the page content;
//   * pages whose only Vue node is the floating contact widget (every VDP and
//     SEO page) defer the chunk to the FIRST USER INTERACTION, same pattern
//     as the GTM/Turnstile deferrals: a real visitor scrolls within moments,
//     the Lighthouse lab run never does — so ~50KB gz of Vue+islands drops
//     out of the PSI "unused JavaScript" audit on 200K+ VDPs.
//
// NOTE: We deliberately do NOT use Vuetify or its MDI font here. None of the
// marketing-vue components actually use Vuetify primitives, and bundling
// Vuetify added 211KB JS + 118KB CSS gzip for zero rendered output.
// =============================================================================
import PriceHistoryScopeController from '../controllers/price_history_scope_controller';
import PriceTrimTableController from '../controllers/price_trim_table_controller';
import { Application as Stimulus } from '@hotwired/stimulus';
import { initClarityDeferred } from '../shared/clarity';
import { persistTheme } from '../shared/theme';
import { registerPresentControllers } from '../shared/marketing_controllers';

// NOTE: the marketing SCSS is no longer imported here. It ships as split
// stylesheet entrypoints (marketing-base/-pages/-seo.scss) linked from the
// marketing layouts, so SEO pages stop paying for the whole-site CSS bundle.

// Marketing-only Stimulus controllers (replaces the global frontend.js
// importmap chain that was loading 30+ inventory/dealer controllers and
// jQuery/Bootstrap on every marketing page render).
import MarketingNavController       from '../controllers/marketing_nav_controller';
import MarketingDropdownController  from '../controllers/marketing_dropdown_controller';
import VinDemoSampleController      from '../controllers/vin_demo_sample_controller';
import CodeTabsController           from '../controllers/code_tabs_controller';
import CopyButtonController         from '../controllers/copy_button_controller';
import PricingTabsController        from '../controllers/pricing_tabs_controller';
import PricingStickyctaController   from '../controllers/pricing_stickycta_controller';
import VinCheckWidgetController     from '../controllers/vin_check_widget_controller';
import MktFlashController           from '../controllers/mkt_flash_controller';
import DeferredQueryController      from '../controllers/deferred_query_controller';
import FinderTeaserController       from '../controllers/finder_teaser_controller';
import PriceConfiguratorController  from '../controllers/price_configurator_controller';
import MileageChartController       from '../controllers/mileage_chart_controller';
import TrendChartController         from '../controllers/trend_chart_controller';
import PaymentCalculatorController  from '../controllers/payment_calculator_controller';
import SeoSearchController          from '../controllers/seo_search_controller';
import ClusterNavController         from '../controllers/cluster_nav_controller';

// Clarity is deferred to the first interaction (10s fallback) on public pages —
// its third-party script is one of the PSI "efficient cache lifetimes" /
// third-party-TBT items on VDP + /prices. See shared/clarity.ts.
initClarityDeferred();

function bootstrapStimulus() {
  if (window.__mkt_stimulus__) return; // HMR guard
  const app = Stimulus.start();
  app.register('marketing-nav',     MarketingNavController);
  app.register('marketing-dropdown', MarketingDropdownController);
  app.register('vin-demo-sample',   VinDemoSampleController);
  app.register('code-tabs',         CodeTabsController);
  app.register('copy-button',       CopyButtonController);
  app.register('pricing-tabs',      PricingTabsController);
  app.register('pricing-stickycta', PricingStickyctaController);
  app.register('vin-check-widget',  VinCheckWidgetController);
  app.register('mkt-flash',         MktFlashController);
  app.register('deferred-query',    DeferredQueryController);
  app.register('finder-teaser',     FinderTeaserController);
  app.register('price-configurator', PriceConfiguratorController);
  app.register('mileage-chart',     MileageChartController);
  app.register('trend-chart',       TrendChartController);
  app.register('price-history-scope', PriceHistoryScopeController);
  app.register('price-trim-table', PriceTrimTableController);
  app.register('payment-calculator', PaymentCalculatorController);
  app.register('seo-search',        SeoSearchController);
  app.register('cluster-nav',       ClusterNavController);
  // These widgets are SSR-only and page-specific. Keep their code out of
  // every VIN/price page; request it as soon as matching markup is present.
  registerPresentControllers(app, {
    'category-browser': () => import('../controllers/category_browser_controller'),
    'depreciation-explorer': () => import('../controllers/depreciation_explorer_controller'),
    'state-browser': () => import('../controllers/state_browser_controller'),
    'inventory-map': () => import('../controllers/inventory_map_controller'),
    'brochure-preview': () => import('../controllers/brochure_preview_controller'),
    'price-watch': () => import('../controllers/price_watch_controller'),
    'value-my-car': () => import('../controllers/value_my_car_controller'),
    'shopper-tools': () => import('../controllers/shopper_tools_controller'),
    'persona-switch': () => import('../controllers/persona_switch_controller'),
    'data-sets-form': () => import('../controllers/data_sets_form_controller'),
    'pricing-product-tabs': () => import('../controllers/pricing_product_tabs_controller'),
    'promo-video': () => import('../controllers/promo_video_controller'),
    'vauto-savings': () => import('../controllers/vauto_savings_controller'),
    'market-report': () => import('../controllers/market_report_controller'),
    'vdp-map': () => import('../marketing/controllers/vdp_map_controller'),
  });
  window.__mkt_stimulus__ = app;
}

// Inline dark-mode toggle — replaces dark_mode_controller (which uses an
// importmap path we no longer load on marketing pages). Toggles both
// `.app-dark` (marketing tokens) and `.dark-mode` (legacy parity), persists
// the choice to localStorage.
function bindDarkModeToggle() {
  const syncThemeControls = () => {
    const dark = document.documentElement.classList.contains('app-dark');
    document.querySelectorAll('[data-dark-mode-button]').forEach((button) => {
      button.setAttribute('aria-pressed', String(dark));
      button.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
    });
    document.querySelectorAll('[data-dark-mode-target="label"]').forEach((label) => {
      label.textContent = dark ? 'Light mode' : 'Dark mode';
    });
  };
  syncThemeControls();
  document.querySelectorAll('[data-action*="dark-mode"], [data-controller="dark-mode"]').forEach((el) => {
    const btn = el.matches('button') ? el : el.querySelector('button');
    if (!btn || btn.__mkt_dark_bound__) return;
    btn.__mkt_dark_bound__ = true;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const root = document.documentElement;
      const next = !root.classList.contains('app-dark');
      root.classList.toggle('app-dark', next);
      root.classList.toggle('dark-mode', next);
      persistTheme(next);
      syncThemeControls();
    });
  });
}

// --- Lazy Vue islands --------------------------------------------------------
// Content islands: the page is not complete without them → load immediately.
const CONTENT_ISLANDS =
  '[data-mkt-vin-demo], [data-mkt-mcp-chat], [data-mkt-dealer-preview], [data-demo-cards]';
// Deferred islands: present on EVERY marketing page but render nothing the
// visitor needs before interacting — the empty hidden #marketing-vue layout
// root (its App.vue only mirrors the dark-mode class, which the inline head
// script + bindDarkModeToggle already handle) and the floating contact
// bubble. Loaded on the first interaction (scroll counts — real visitors
// reach it within moments; the PSI lab run never interacts).
const DEFERRED_ISLANDS = '#marketing-vue, [data-mkt-contact-widget]';

let islandsRequested = false;
function loadVueIslands() {
  if (islandsRequested) return;
  islandsRequested = true;
  import('../marketing-vue/mount_islands')
    .then((m) => m.mountVueIslands())
    .catch((e) => console.error('[marketing-vue] islands chunk load failed:', e));
}

function scheduleVueIslands() {
  if (document.querySelector(CONTENT_ISLANDS)) {
    loadVueIslands();
    return;
  }
  if (!document.querySelector(DEFERRED_ISLANDS)) return;

  const opts = { passive: true };
  const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
  const onFirstInteraction = () => {
    events.forEach((e) => window.removeEventListener(e, onFirstInteraction, opts));
    loadVueIslands();
  };
  events.forEach((e) => window.addEventListener(e, onFirstInteraction, opts));
}

function mountAll() {
  bootstrapStimulus();
  bindDarkModeToggle();
  scheduleVueIslands();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll);
} else {
  // HMR reload after DOMContentLoaded — mount immediately.
  mountAll();
}
